/**
 * versionState.js - Módulo puente para compartir estado entre composable y guard
 *
 * Este módulo permite que el GuardRouter (que no tiene acceso a Vue composables)
 * pueda comunicarse con useVersionCheck sin depender de Vue.
 */

// Import config for loop prevention constants
import { VERSION_CONFIG } from '@/config/version';

let staleFlag = false;
let handleStalenessFn = null;
let checkVersionFn = null;

/**
 * Marca si la app está desactualizada
 * @param {boolean} value
 */
export const setStaleFlag = (value) => {
  staleFlag = value;
};

/**
 * Obtiene si la app está desactualizada
 * @returns {boolean}
 */
export const getStaleFlag = () => staleFlag;

/**
 * Registra la función para manejar cuando la app está stale
 * @param {Function} fn
 */
export const setHandleStaleness = (fn) => {
  handleStalenessFn = fn;
};

/**
 * Obtiene la función para manejar staleness
 * @returns {Function|null}
 */
export const getHandleStaleness = () => handleStalenessFn;

/**
 * Registra la función de checkVersion desde el composable
 * @param {Function} fn
 */
export const setCheckVersionFn = (fn) => {
  checkVersionFn = fn;
};

/**
 * Obtiene la función de checkVersion
 * @returns {Function|null}
 */
export const getCheckVersionFn = () => checkVersionFn;

/**
 * Dispara una verificación de versión si está disponible
 * No bloquea, solo dispara (fire-and-forget)
 */
export const triggerVersionCheck = () => {
  if (checkVersionFn) {
    checkVersionFn();
  }
};

/**
 * Gets reload timestamps from sessionStorage for loop prevention
 * @returns {number[]}
 */
const getReloadTimestamps = () => {
  try {
    const stored = sessionStorage.getItem(VERSION_CONFIG.STORAGE_KEYS.RELOAD_TIMESTAMPS);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Saves reload timestamps to sessionStorage
 * @param {number[]} timestamps
 */
const setReloadTimestamps = (timestamps) => {
  try {
    sessionStorage.setItem(
      VERSION_CONFIG.STORAGE_KEYS.RELOAD_TIMESTAMPS,
      JSON.stringify(timestamps),
    );
  } catch {
    // sessionStorage not available
  }
};

/**
 * Checks if we can auto-reload without entering a loop
 * @returns {boolean}
 */
const canAutoReload = () => {
  const now = Date.now();
  const windowMs = VERSION_CONFIG.RELOAD_LOOP_WINDOW_MS;
  const timestamps = getReloadTimestamps().filter((ts) => now - ts < windowMs);
  setReloadTimestamps(timestamps);
  return timestamps.length < VERSION_CONFIG.MAX_AUTO_RELOADS;
};

/**
 * Records a reload timestamp and performs the reload
 */
const performFallbackReload = () => {
  const now = Date.now();
  const windowMs = VERSION_CONFIG.RELOAD_LOOP_WINDOW_MS;
  const timestamps = getReloadTimestamps().filter((ts) => now - ts < windowMs);
  timestamps.push(now);
  setReloadTimestamps(timestamps);
  window.location.reload();
};

/**
 * Maneja la condición de staleness si la función está disponible
 * Si no está disponible, hace reload con protección de loop como fallback
 */
export const handleStalenessOrReload = () => {
  if (handleStalenessFn) {
    handleStalenessFn();
  } else {
    // Fallback: reload with loop prevention if composable hasn't initialized
    if (canAutoReload()) {
      performFallbackReload();
    } else {
      // We're in a reload loop without the composable - just log and don't reload
      // The composable will mount eventually and show the modal
      console.warn('Reload loop detected in fallback, waiting for composable to mount');
    }
  }
};
